import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

// Supported event kinds for Tombstone — massively expanded across NIPs
export const TOMBSTONE_KINDS = {
  NOTE: 1,
  REPOST: 6,
  REACTION: 7,
  GENERIC_REPOST: 16,
  ZAP: 9735,
  PICTURE: 20,
  VIDEO: 21,
  SHORT_VIDEO: 22,
  PUBLIC_MESSAGE: 24,
  LONG_FORM: 30023,
  DRAFT_LONG_FORM: 30024,
  COMMENT: 1111,
  HIGHLIGHT: 9802,
  WIKI: 30818,
  CODE_SNIPPET: 1337,
  POLL: 1068,
  REPORT: 1984,
  LABEL: 1985,
  BADGE_DEFINITION: 30009,
  PODCAST_METADATA: 10154,
  CLASSIFIED_LISTING: 30402,
  DRAFT_CLASSIFIED: 30403,
  LIVE_EVENT: 30311,
  INTERACTIVE_ROOM: 30312,
  CONFERENCE_EVENT: 30313,
  CALENDAR_DATE_EVENT: 31922,
  CALENDAR_TIME_EVENT: 31923,
  CALENDAR: 31924,
  CALENDAR_RSVP: 31925,
  GIT_REPOSITORY: 30617,
  GIT_REPOSITORY_STATE: 30618,
  ADDRESSABLE_VIDEO: 34235,
  ADDRESSABLE_SHORT_VIDEO: 34236,
  DRAFT_EVENT: 31234,
  FOLLOW_LIST: 3,
  MUTE_LIST: 10000,
  PINNED_NOTES: 10001,
  BOOKMARKS: 10003,
  RELAY_LIST: 10002,
  COMMUNITIES_LIST: 10004,
  PUBLIC_CHATS_LIST: 10005,
  BLOCKED_RELAYS_LIST: 10006,
  SEARCH_RELAYS_LIST: 10007,
  USER_GROUPS: 10009,
  INTERESTS_LIST: 10015,
  MEDIA_FOLLOWS: 10020,
  USER_EMOJI_LIST: 10030,
  DM_RELAY_LIST: 10050,
  FAVORITE_PODCASTS: 10054,
  USER_SERVER_LIST: 10063,
  AUTHORED_PODCASTS: 10064,
  FOLLOW_SETS: 30000,
  BOOKMARK_SETS: 30003,
  CURATION_SETS: 30004,
  RELAY_SETS: 30002,
  VIDEO_SETS: 30005,
  PICTURE_SETS: 30006,
  KIND_MUTE_SETS: 30007,
  BADGE_SETS: 30008,
  INTEREST_SETS: 30015,
  EMOJI_SETS: 30030,
  APP_CURATION_SETS: 30267,
  STARTER_PACKS: 39089,
  MEDIA_STARTER_PACKS: 39092,
  USER_STATUS: 30315,
  DELETION: 5,
  MASTER_TOMBSTONE: 30078,
} as const;

export type EventKind = typeof TOMBSTONE_KINDS[keyof typeof TOMBSTONE_KINDS];

export interface ScannedEvent {
  event: NostrEvent;
  kind: EventKind;
  label: string;
  isDeleted: boolean;
  deletionEventId?: string;
  isAddressable: boolean;
  addrCoord?: string;
}

export interface ScanInfo {
  pagesScanned: number;
  totalEventsFound: number;
  finalPageSize: number;
  mainRelayPages: number;
  discoveryRelayPages: number;
}

function getEventLabel(kind: number): string {
  switch (kind) {
    // Regular content
    case 1: return 'Note';
    case 6: return 'Repost';
    case 7: return 'Reaction';
    case 16: return 'Generic Repost';
    case 20: return 'Picture';
    case 21: return 'Video';
    case 22: return 'Short Video';
    case 24: return 'Public Message';
    case 1111: return 'Comment';
    case 9802: return 'Highlight';
    case 30818: return 'Wiki Article';
    case 1337: return 'Code Snippet';
    case 1068: return 'Poll';
    case 1984: return 'Report';
    case 1985: return 'Label';
    case 10154: return 'Podcast';

    // Long-form & drafts
    case 30023: return 'Article';
    case 30024: return 'Draft Article';
    case 31234: return 'Draft Event';

    // Commerce
    case 30402: return 'Classified Listing';
    case 30403: return 'Draft Listing';

    // Live streaming
    case 30311: return 'Live Event';
    case 30312: return 'Interactive Room';
    case 30313: return 'Conference Event';

    // Calendar
    case 31922: return 'Calendar Date Event';
    case 31923: return 'Calendar Time Event';
    case 31924: return 'Calendar';
    case 31925: return 'Calendar RSVP';

    // Git
    case 30617: return 'Git Repository';
    case 30618: return 'Git Repository State';

    // Badges
    case 30009: return 'Badge Definition';

    // Addressable media
    case 34235: return 'Addressable Video';
    case 34236: return 'Addressable Short Video';

    // Replaceable lists
    case 3: return 'Follow List';
    case 10000: return 'Mute List';
    case 10001: return 'Pinned Notes';
    case 10002: return 'Relay List';
    case 10003: return 'Bookmarks';
    case 10004: return 'Communities List';
    case 10005: return 'Public Chats List';
    case 10006: return 'Blocked Relays List';
    case 10007: return 'Search Relays List';
    case 10009: return 'User Groups';
    case 10015: return 'Interests List';
    case 10020: return 'Media Follows';
    case 10030: return 'User Emoji List';
    case 10050: return 'DM Relay List';
    case 10054: return 'Favorite Podcasts';
    case 10063: return 'User Server List';
    case 10064: return 'Authored Podcasts';
    case 30315: return 'User Status';

    // Addressable sets
    case 30000: return 'Follow Set';
    case 30002: return 'Relay Set';
    case 30003: return 'Bookmark Set';
    case 30004: return 'Curation Set';
    case 30005: return 'Video Set';
    case 30006: return 'Picture Set';
    case 30007: return 'Kind Mute Set';
    case 30008: return 'Badge Set';
    case 30015: return 'Interest Set';
    case 30030: return 'Emoji Set';
    case 30267: return 'App Curation Set';
    case 39089: return 'Starter Pack';
    case 39092: return 'Media Starter Pack';

    // System
    case 5: return 'Deletion Request';
    case 30078: return 'Tombstone Master';
    default: return `Kind ${kind}`;
  }
}

function isAddressableKind(kind: number): boolean {
  return kind >= 30000 && kind < 40000;
}

function isReplaceableKind(kind: number): boolean {
  // kind 1 (short notes) should only use Delete, not Nullify
  // We only want to show Nullify for lists, sets, articles, etc.
  return (kind >= 10000 && kind < 20000) || kind === 0 || kind === 3 || kind === 30315;
}

export function getAddrCoord(event: NostrEvent): string | undefined {
  if (!isAddressableKind(event.kind)) return undefined;
  const d = event.tags.find(([name]) => name === 'd')?.[1] ?? '';
  return `${event.kind}:${event.pubkey}:${d}`;
}

// Public discovery relays used for wider scanning.
const DISCOVERY_RELAYS = [
  'wss://relay.primal.net',
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.nostr.band',
  'wss://nostr.wine',
  'wss://relay.ditto.pub',
  'wss://purple.pages.dev',
  'wss://relay.snort.social',
  'wss://eden.nostr.land',
  'wss://relay.current.fyi',
  'wss://nostr.rocks',
  'wss://relay.nostr.bg',
  'wss://nostr.mom',
  'wss://nostr.oxtr.dev',
  'wss://relay.bitcoiner.social',
  'wss://relay.nostr.wf',
  'wss://nostr-pub.wellorder.net',
  'wss://nostr.orangepill.dev',
];

export interface ScanResult {
  events: ScannedEvent[];
  deletedIds: Set<string>;
  deletedCoords: Set<string>;
  deletionEvents: NostrEvent[];
  masterTombstone: NostrEvent | null;
  scanInfo: ScanInfo;
}

/** Deeply paginate a Nostr query across kind chunks until exhausted. */
async function deepPaginatedQuery(
  queryable: any,
  kindChunks: number[][],
  pubkey: string,
  maxPages: number,
  pageLimit: number
): Promise<{ events: NostrEvent[]; pagesScanned: number }> {
  const allEvents: NostrEvent[] = [];
  let until: number | undefined = undefined;
  let page = 0;

  while (page < maxPages) {
    const filters = kindChunks.map((chunk) => {
      const f: any = {
        kinds: chunk,
        authors: [pubkey],
        limit: Math.ceil(pageLimit / kindChunks.length),
      };
      if (until !== undefined) f.until = until;
      return f;
    });

    const pageEvents: NostrEvent[] = await queryable.query(filters);

    if (pageEvents.length === 0) break;

    allEvents.push(...pageEvents);

    const oldest = pageEvents.reduce((oldest, event) =>
      event.created_at < oldest.created_at ? event : oldest
    );
    until = oldest.created_at - 1;
    page++;
  }

  return { events: allEvents, pagesScanned: page };
}

export function useUserEvents(pubkey: string | undefined, scanWider = false) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['tombstone-events', pubkey, scanWider],
    enabled: !!pubkey,
    queryFn: async () => {
      if (!pubkey) {
        return {
          events: [],
          deletedIds: new Set<string>(),
          deletedCoords: new Set<string>(),
          masterTombstone: null,
          scanInfo: { pagesScanned: 0, totalEventsFound: 0, finalPageSize: 0 },
        } as ScanResult;
      }

      const supportedKinds = [
        // Social / interactions
        1, 6, 7, 16, 1111,
        // Media
        20, 21, 22, 34235, 34236,
        // Content
        24, 9802, 30818, 1337, 1068, 1984, 1985, 10154,
        // Long-form & drafts
        30023, 30024, 31234,
        // Commerce
        30402, 30403,
        // Live streaming
        30311, 30312, 30313,
        // Calendar
        31922, 31923, 31924, 31925,
        // Git
        30617, 30618,
        // Badges
        30009,
        // Replaceable lists
        3, 10000, 10001, 10002, 10003, 10004, 10005, 10006, 10007, 10009, 10015, 10020, 10030, 10050, 10054, 10063, 10064, 30315,
        // Addressable sets
        30000, 30002, 30003, 30004, 30005, 30006, 30007, 30008, 30015, 30030, 30267, 39089, 39092,
      ];

      // Break kinds into chunks — most relays reject filters with too many kinds.
      const KIND_CHUNK_SIZE = 10;
      const kindChunks: number[][] = [];
      for (let i = 0; i < supportedKinds.length; i += KIND_CHUNK_SIZE) {
        kindChunks.push(supportedKinds.slice(i, i + KIND_CHUNK_SIZE));
      }

      // ── Deep scan main pool (user's read relays) ──
      const mainScan = await deepPaginatedQuery(nostr, kindChunks, pubkey, 50, 750);

      // ── Deep scan discovery relays (optional wider scan) ──
      let discoveryScan = { events: [] as NostrEvent[], pagesScanned: 0 };
      if (scanWider) {
        const discoveryGroup = nostr.group(DISCOVERY_RELAYS);
        discoveryScan = await deepPaginatedQuery(discoveryGroup, kindChunks, pubkey, 30, 750);
      }

      // Merge raw events from all sources, deduplicating by event id
      const rawEventMap = new Map<string, NostrEvent>();
      for (const event of [...mainScan.events, ...discoveryScan.events]) {
        const existing = rawEventMap.get(event.id);
        if (!existing || event.created_at > existing.created_at) {
          rawEventMap.set(event.id, event);
        }
      }
      const allEvents = Array.from(rawEventMap.values());

      // Fetch deletions & master tombstone from main pool
      const [deletions, masterEvents] = await Promise.all([
        nostr.query([{
          kinds: [5],
          authors: [pubkey],
          limit: 1000,
        }]),
        nostr.query([{
          kinds: [30078],
          authors: [pubkey],
          '#d': ['tombstone-master'],
          limit: 1,
        }]),
      ]);

      // Build sets of deleted IDs and addr coords
      const deletedIds = new Set<string>();
      const deletedCoords = new Set<string>();
      const deletionMap = new Map<string, string>();

      for (const del of deletions) {
        for (const tag of del.tags) {
          if (tag[0] === 'e' && tag[1]) {
            deletedIds.add(tag[1]);
            deletionMap.set(tag[1], del.id);
          }
          if (tag[0] === 'a' && tag[1]) {
            deletedCoords.add(tag[1]);
            deletionMap.set(tag[1], del.id);
          }
        }
      }

      // Group by logical identity (d-tag for addressable, event id for regular)
      const eventMap = new Map<string, NostrEvent>();
      for (const event of allEvents) {
        const addrCoord = getAddrCoord(event);
        const key = addrCoord || event.id;
        const existing = eventMap.get(key);
        if (!existing || event.created_at > existing.created_at) {
          eventMap.set(key, event);
        }
      }

      const scanned: ScannedEvent[] = Array.from(eventMap.values()).map((event) => {
        const addrCoord = getAddrCoord(event);
        const isDeleted = deletedIds.has(event.id) || (addrCoord ? deletedCoords.has(addrCoord) : false);
        const deletionKey = addrCoord && deletedCoords.has(addrCoord) ? addrCoord : event.id;

        return {
          event,
          kind: event.kind as EventKind,
          label: getEventLabel(event.kind),
          isDeleted,
          deletionEventId: isDeleted ? deletionMap.get(deletionKey) : undefined,
          isAddressable: isAddressableKind(event.kind),
          addrCoord,
        };
      });

      scanned.sort((a, b) => b.event.created_at - a.event.created_at);

      const totalPages = mainScan.pagesScanned + discoveryScan.pagesScanned;

      return {
        events: scanned,
        deletedIds,
        deletedCoords,
        deletionEvents: deletions,
        masterTombstone: masterEvents[0] || null,
        scanInfo: {
          pagesScanned: totalPages,
          totalEventsFound: allEvents.length,
          finalPageSize: allEvents.length > 0 ? mainScan.events.length : 0,
          mainRelayPages: mainScan.pagesScanned,
          discoveryRelayPages: discoveryScan.pagesScanned,
        } as ScanInfo,
      };
    },
    staleTime: 30000,
  });
}

export function useRelayEventStatus(
  eventId: string,
  addrCoord: string | undefined,
  relays: string[]
) {
  return useQuery({
    queryKey: ['relay-status', eventId, relays.join(',')],
    enabled: relays.length > 0,
    queryFn: async () => {
      const results: Record<string, 'found' | 'not-found' | 'error'> = {};

      await Promise.all(
        relays.map(async (relayUrl) => {
          try {
            const url = relayUrl.replace(/^wss?:\/\//, '');
            const ws = new WebSocket(relayUrl);

            const result = await new Promise<'found' | 'not-found' | 'error'>((resolve) => {
              const timeout = setTimeout(() => {
                ws.close();
                resolve('error');
              }, 6000);

              ws.onopen = () => {
                const filter = addrCoord
                  ? { kinds: [parseInt(addrCoord.split(':')[0])], authors: [addrCoord.split(':')[1]], '#d': [addrCoord.split(':')[2]], limit: 1 }
                  : { ids: [eventId], limit: 1 };

                ws.send(JSON.stringify(['REQ', 'check-' + eventId.slice(0, 8), filter]));
              };

              ws.onmessage = (msg) => {
                try {
                  const data = JSON.parse(msg.data);
                  if (data[0] === 'EVENT') {
                    clearTimeout(timeout);
                    ws.close();
                    resolve('found');
                  } else if (data[0] === 'EOSE') {
                    clearTimeout(timeout);
                    ws.close();
                    resolve('not-found');
                  }
                } catch { /* ignore */ }
              };

              ws.onerror = () => {
                clearTimeout(timeout);
                resolve('error');
              };

              ws.onclose = () => {
                clearTimeout(timeout);
              };
            });

            results[relayUrl] = result;
          } catch {
            results[relayUrl] = 'error';
          }
        })
      );

      return results;
    },
    staleTime: 10000,
    retry: false,
  });
}

export { getEventLabel, isAddressableKind, isReplaceableKind };
